import { b as buildPaginationParams, n as normalizeToPaginated } from './chunks/pagination-utils-9vQ-Npkr.js';

/**
 * Platform-Agnostic TriggerSource API Client
 *
 * Handles all trigger source operations:
 * - CRUD operations for trigger sources (QR codes, NFC tags, GPS geofences, webhooks, etc.)
 * - Trigger sources are independent entities that can be assigned to campaigns
 *
 * Uses @explorins/pers-shared DTOs for consistency with backend.
 */
class TriggerSourceApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }
    /**
     * PUBLIC: Get trigger sources with optional filters and pagination
     *
     * @param options - Filter and pagination options (includes location filters)
     * @returns Paginated list of trigger sources
     *
     * @example
     * ```typescript
     * // Get all QR code trigger sources
     * const qrSources = await triggerSourceApi.getTriggerSources({ type: 'QR_CODE' });
     *
     * // Get trigger sources for a specific campaign
     * const campaignSources = await triggerSourceApi.getTriggerSources({
     *   campaignId: 'campaign-123',
     *   page: 1,
     *   limit: 20
     * });
     *
     * // Filter by location (geocoding)
     * const dubaiSources = await triggerSourceApi.getTriggerSources({
     *   city: 'Dubai',
     *   countryCode: 'AE'
     * });
     * ```
     */
    async getTriggerSources(options) {
        const params = buildPaginationParams(options);
        if (options?.type)
            params.set('type', options.type);
        if (options?.businessId)
            params.set('businessId', options.businessId);
        if (options?.campaignId)
            params.set('campaignId', options.campaignId);
        if (options?.active !== undefined)
            params.set('active', String(options.active));
        if (options?.includeDeleted)
            params.set('includeDeleted', String(options.includeDeleted));
        // Location filter options (geocoding)
        if (options?.city)
            params.set('city', options.city);
        if (options?.countryCode)
            params.set('countryCode', options.countryCode);
        if (options?.country)
            params.set('country', options.country);
        if (options?.neighborhood)
            params.set('neighborhood', options.neighborhood);
        if (options?.district)
            params.set('district', options.district);
        const response = await this.apiClient.get(`/trigger-sources?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * PUBLIC: Get trigger source by ID
     *
     * @param triggerSourceId - UUID of the trigger source
     * @returns Trigger source details
     */
    async getTriggerSourceById(triggerSourceId) {
        return this.apiClient.get(`/trigger-sources/${triggerSourceId}`);
    }
    /**
     * ADMIN: Create a new trigger source
     *
     * @param triggerSource - Trigger source creation data
     * @returns Created trigger source
     *
     * @example
     * ```typescript
     * const qrSource = await triggerSourceApi.createTriggerSource({
     *   type: 'QR_CODE',
     *   name: 'Store Entrance QR',
     *   description: 'QR code at main entrance',
     *   businessId: 'business-123',
     *   coordsLatitude: 47.6062,
     *   coordsLongitude: -122.3321
     * });
     * ```
     */
    async createTriggerSource(triggerSource) {
        return this.apiClient.post('/trigger-sources', triggerSource);
    }
    /**
     * ADMIN: Update a trigger source
     *
     * @param triggerSourceId - UUID of the trigger source to update
     * @param triggerSource - Updated trigger source data (partial)
     * @returns Updated trigger source
     */
    async updateTriggerSource(triggerSourceId, triggerSource) {
        return this.apiClient.put(`/trigger-sources/${triggerSourceId}`, triggerSource);
    }
    /**
     * ADMIN: Delete (soft delete) a trigger source
     *
     * @param triggerSourceId - UUID of the trigger source to delete
     * @returns Success status
     */
    async deleteTriggerSource(triggerSourceId) {
        return this.apiClient.delete(`/trigger-sources/${triggerSourceId}`);
    }
}

/**
 * Platform-Agnostic TriggerSource Service
 *
 * Contains trigger source business logic and operations that work across platforms.
 * No framework dependencies - pure TypeScript business logic.
 */
class TriggerSourceService {
    constructor(triggerSourceApi) {
        this.triggerSourceApi = triggerSourceApi;
    }
    /**
     * Get trigger sources with optional filters
     */
    async getTriggerSources(options) {
        return this.triggerSourceApi.getTriggerSources(options);
    }
    /**
     * Get trigger source by ID
     */
    async getTriggerSourceById(triggerSourceId) {
        return this.triggerSourceApi.getTriggerSourceById(triggerSourceId);
    }
    /**
     * ADMIN: Create a new trigger source
     */
    async createTriggerSource(triggerSource) {
        return this.triggerSourceApi.createTriggerSource(triggerSource);
    }
    /**
     * ADMIN: Update a trigger source
     */
    async updateTriggerSource(triggerSourceId, triggerSource) {
        return this.triggerSourceApi.updateTriggerSource(triggerSourceId, triggerSource);
    }
    /**
     * ADMIN: Delete a trigger source
     */
    async deleteTriggerSource(triggerSourceId) {
        return this.triggerSourceApi.deleteTriggerSource(triggerSourceId);
    }
}

export { TriggerSourceApi, TriggerSourceService };
//# sourceMappingURL=trigger-source.js.map
